/**
 * SortablePhotoGrid — iter v33 r26
 * ────────────────────────────────────────────────────────────────────────
 * User: «tengo premuto sulla foto e posso muoverla. Perche' cosi' e' troppo
 * incasinato. Frecce troppo grandi».
 *
 * Griglia di miniature ordinabile con tap-and-hold (mobile) + drag-and-drop
 * (desktop). Zero pulsanti, zero frecce, zero toast di conferma.
 *
 * Attivazione drag:
 *   - Touch (dito/pennino):  tenere premuto 250ms senza muoversi (o muovendo
 *                            meno di 8px) → parte il drag. Un tap normale
 *                            (release <250ms) fa lo stesso di prima (open/select).
 *   - Mouse (desktop):       muovere >5px dopo il mousedown → parte il drag.
 *
 * Salvataggio silenzioso: chiama `onReorder(orderedIds)` che gia' fa optimistic
 * update + PATCH backend + retry silenzioso in background.
 */
import React from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/**
 * Wrapper della singola miniatura: applica trasformazione drag e passa
 * `listeners`/`attributes` di dnd-kit al `children` tramite render-prop.
 *
 * `disabled` → quando la modalita' "Seleziona" e' attiva, il drag e' spento
 * (l'agente sta selezionando piu' foto, non riordinando).
 */
function SortableItem({ id, disabled, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // iter v33 r54 · User: «clicco sulla foto e non me la apre subito,
    // serve un doppio tap». Root cause: `touchAction: "auto"` su iOS
    // WebView fa attendere ~300ms per il double-tap-zoom prima di
    // sparare il click. Con "manipulation" iOS spegne il double-tap-zoom
    // e il pinch-zoom ma tiene lo scroll verticale/orizzontale libero:
    // il tap sulla thumb apre l'anteprima grande ISTANTANEAMENTE.
    // Il drag resta comunque agganciato solo all'handle .drag-handle,
    // che ha touchAction: "none" per intercettare il gesto di riordino.
    touchAction: "manipulation",
    zIndex: isDragging ? 30 : "auto",
    opacity: isDragging ? 0.85 : 1,
    boxShadow: isDragging
      ? "0 18px 40px -12px rgba(28,28,26,0.35), 0 6px 14px -6px rgba(28,28,26,0.25)"
      : undefined,
    scale: isDragging ? "1.04" : "1",
  };

  return children({
    setNodeRef,
    style,
    attributes,
    listeners,
    isDragging,
  });
}

/**
 * `items` → array di id (stringhe) nell'ordine attuale.
 * `renderItem(id, index, dragHandle)` → funzione che rende la thumb.
 *      `dragHandle` = { setNodeRef, style, attributes, listeners, isDragging }
 *      L'app "monta" questi props sul wrapper esterno della thumb.
 * `onReorder(newIdsInOrder)` → chiamato quando l'utente rilascia il drag
 *      con un ordine diverso da quello iniziale.
 * `disabled` → se true, il drag e' spento (es. selectMode ON).
 */
export default function SortablePhotoGrid({
  items,
  renderItem,
  onReorder,
  disabled = false,
  className = "",
  gridTestid = "sortable-photo-grid",
}) {
  const sensors = useSensors(
    // Desktop: mouse deve muoversi >5px prima di iniziare il drag, cosi'
    // un semplice click resta un click.
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    // Mobile: 150ms di pressione fermo sull'handle → parte il drag.
    // iter v33 r48 · Adesso i listener del drag sono solo sull'handle
    // (piccolo puntino in alto a destra della thumb). Il touch sulla
    // thumb in se' NON attiva mai il drag. Quindi il delay puo' essere
    // corto: chi tocca l'handle ha gia' deciso di riordinare. Un tap
    // accidentale sull'handle e' molto raro (28x28px in un angolo).
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.indexOf(active.id);
    const newIndex = items.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(items, oldIndex, newIndex);
    if (typeof onReorder === "function") onReorder(next);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={rectSortingStrategy}>
        <div className={className} data-testid={gridTestid}>
          {items.map((id, idx) => (
            <SortableItem key={id} id={id} disabled={disabled}>
              {(handle) => renderItem(id, idx, handle)}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
